/**
 * Per-component prop metadata that is NOT expressible in the zod schemas:
 *
 * - `componentPropAliases`: commonly hallucinated prop names and the
 *   canonical prop they map to. Applied by `normalize.ts` (backend at
 *   write time, frontend as a render-time safety net for already-persisted
 *   specs). Keep this table SMALL — only drift actually observed in agent
 *   output. It is a repair mechanism, not a permissive shadow schema; the
 *   agent still gets a warning telling it the canonical name.
 *
 * - `componentCoreProps`: compact must-have signature per component, used
 *   by the agent-core prompt enricher so agents see the props a component
 *   cannot work without — WITHOUT inlining the full reference doc.
 *   Syntax: `a|b` = at least one of, `a, b` = both, '' = none required.
 *
 * No imports beyond component-types — portable to the agent-core backend
 * (vendored via `npm run aui-schemas:sync`) and zod-free so the frontend
 * can import it without pulling zod into render chunks.
 */

import type { ComponentType } from './component-types';

export interface PropAlias {
  /** Canonical prop the alias maps to. */
  target: string;
  /** Optional value transform (e.g. readOnly → !editable). */
  map?: (value: unknown) => unknown;
}

export const componentPropAliases: Partial<
  Record<ComponentType, Record<string, PropAlias>>
> = {
  MarkdownEditor: {
    source: { target: 'contentUrl' },
    url: { target: 'contentUrl' },
    readOnly: {
      target: 'editable',
      map: (v) => (typeof v === 'boolean' ? !v : v),
    },
  },
};

/**
 * `satisfies Record<ComponentType, string>` keeps this exhaustive: a new
 * component type fails the build here until its must-have props (or '')
 * are declared.
 */
export const componentCoreProps = {
  Column: '',
  Row: '',
  Grid: '',
  Text: 'text|content|textUrl',
  Card: '',
  Badge: 'text',
  Alert: 'title|description',
  Button: 'label',
  Tabs: 'tabs',
  MarkdownEditor: 'content|contentUrl',
  Table: 'columns, rows|rowsUrl',
  StatCard: 'title, value',
  StatsOverview: '',
  ChartCard: '',
  Sparkline: 'data',
  AreaChart: 'option|optionUrl',
  BarChart: 'option|optionUrl',
  LineChart: 'option|optionUrl',
  PieChart: 'option|optionUrl',
  RadarChart: 'option|optionUrl',
  ScatterChart: 'option|optionUrl',
  TreemapChart: 'option|optionUrl',
  GeoMapChart: 'mapName, data',
  TaskDashboard: '',
  AgentTaskList: '',
  Files: '',
  Calendar: 'events|eventsUrl',
} satisfies Record<ComponentType, string>;
